// Thin wrapper over the Web Speech API (SpeechRecognition) for dictation.
// Chrome/Edge/Safari support this natively; we degrade gracefully elsewhere.

const Recognition =
  typeof window !== 'undefined' &&
  (window.SpeechRecognition || window.webkitSpeechRecognition)

export const speechSupported = () => Boolean(Recognition)

// Creates a dictation session. Callbacks:
//   onResult(finalText, interimText) — finalText is newly finalized speech
//     since the last final result; interimText is the live in-progress guess.
//   onEnd() — recognition stopped (user, error, or silence timeout).
// pause() aborts recognition so nothing said while paused lands; resume()
// starts it again (there is no holding a SpeechRecognition open).
export const createDictation = ({onResult, onEnd}) => {
  if (!Recognition) return null

  const recognition = new Recognition()
  recognition.continuous = true
  recognition.interimResults = true
  recognition.lang = navigator.language || 'en-US'

  let stopped = false
  let ended = false

  // A failure fires both onerror and onend, so make sure callers only ever
  // see one onEnd per session.
  const endOnce = () => {
    if (ended) return

    ended = true
    onEnd()
  }

  recognition.onresult = event => {
    let finalText = ''
    let interimText = ''

    for (let i = event.resultIndex; i < event.results.length; i++) {
      const transcript = event.results[i][0].transcript

      if (event.results[i].isFinal) finalText += transcript
      else interimText += transcript
    }

    onResult(finalText, interimText)
  }

  recognition.onerror = () => {
    stopped = true
    endOnce()
  }

  recognition.onend = () => {
    // Chrome ends recognition after ~60s of audio or a few seconds of
    // silence; restart transparently unless we were told to stop.
    if (stopped) {
      endOnce()
    } else {
      try {
        recognition.start()
      } catch {
        endOnce()
      }
    }
  }

  return {
    start: () => {
      stopped = false
      ended = false
      try {
        recognition.start()
      } catch { /* already started */ }
    },
    stop: () => {
      stopped = true
      try {
        recognition.stop()
      } catch { /* already stopped */ }
    },
    pause: () => {
      stopped = true
      try {
        recognition.abort()
      } catch { /* already stopped */ }
    },
    resume: () => {
      stopped = false
      ended = false
      try {
        recognition.start()
      } catch { /* already started */ }
    }
  }
}
